import React from 'react'

import ChainToken from '@src/components/ChainToken'
import CopyToClipboard from '@src/components/CopyToClipboard'
import ClockIcon from '@src/components/icons/Clock'
import ExchangeIcon from '@src/components/icons/Exchange'
import PointIcon from '@src/components/icons/Point'
import SuccessIcon from '@src/components/icons/Success'
import UpArrowCorner from '@src/components/icons/UpArrowCorner'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { formatExchangeAmountWithCommas } from '@src/utils/exchangeUtils'
import { formatNumber } from '@src/utils/formatFee'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useConfigStore } from '@src/zustand_stores'

interface IFeatherSwapCompletedProps {
    sellData: IExchangeData
    buyData: IExchangeData
    fees: IFee[]
    orderId: string
    miles?: number
    result?: {
        sourceTxHash?: string
        destinationTxHash?: string
        message?: string
    }
    fullTradeTimeTimer: any
}

const FeatherSwapCompleted: React.FC<IFeatherSwapCompletedProps> = ({
    sellData,
    buyData,
    fees,
    orderId,
    miles,
    result = undefined,
    fullTradeTimeTimer,
}) => {
    const availableNetworks = useConfigStore(config => config.availableNetworks)

    // Get transaction hashes
    const sourceTxHash = result?.sourceTxHash?.includes('/')
        ? result.sourceTxHash.substring(result.sourceTxHash.lastIndexOf('/') + 1)
        : result?.sourceTxHash

    const destinationTxHash = result?.destinationTxHash?.includes('/')
        ? result.destinationTxHash.substring(result.destinationTxHash.lastIndexOf('/') + 1)
        : result?.destinationTxHash

    // Format the transaction hashes for display
    const formattedSourceTxHash = sourceTxHash ? truncateAddress(sourceTxHash) : ''
    const formattedDestinationTxHash = destinationTxHash ? truncateAddress(destinationTxHash) : ''

    // Build explorer URLs if not already full URLs
    const sourceTxExplorerUrl = result?.sourceTxHash?.startsWith('http')
        ? result.sourceTxHash
        : sourceTxHash
          ? `${getBlockExplorerUrlFromChainId(sellData.chainId.toString())}/tx/${sourceTxHash}`
          : ''

    const destinationTxExplorerUrl = result?.destinationTxHash?.startsWith('http')
        ? result.destinationTxHash
        : destinationTxHash
          ? `${getBlockExplorerUrlFromChainId(buyData.chainId.toString())}/tx/${destinationTxHash}`
          : ''

    // Format the time string
    const formatTime = () => {
        if (!fullTradeTimeTimer) return '0s'

        const { seconds, minutes, hours } = fullTradeTimeTimer
        if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s`
        } else if (minutes > 0) {
            return `${minutes}m ${seconds}s`
        } else {
            return `${seconds}s`
        }
    }

    return (
        <div className='px-4'>
            <div className='space-y-4'>
                <div className='flex w-full items-center justify-center gap-[6px]'>
                    <SuccessIcon />
                    <p className='flex-10 text-base font-medium text-mach-green'>Swap Complete</p>
                </div>
                <div className='relative flex'>
                    <img src={'/assets/image/ticket.png'} alt='' />
                    <div className='absolute left-0 top-0 h-full w-full p-4'>
                        <div className='flex flex-col items-center justify-center gap-4'>
                            <img src={'/assets/image/plane.png'} alt='' />
                            <div
                                className='relative h-[131px] w-full rounded-lg border-[]'
                                style={{
                                    background:
                                        'conic-gradient(from 180deg at 50% 50%, #3E73C41A 0deg, #349FFD1A 144deg, rgba(52, 159, 253, 0.1) 227.82deg, #3E73C41A 360deg)',
                                    boxShadow:
                                        '0px 4px 4px rgba(0, 0, 0, 0.25), 0px -1px 1px #000000, inset 0px 1px 0px rgba(255, 255, 255, 0.25)',
                                }}
                            >
                                <div className='absolute inset-0 rounded-xl border border-[#349FFD] blur-[2px]'></div>
                                <div className='mt-[43px] flex gap-[10px] px-[15px]'>
                                    <div className='flex flex-1 flex-col gap-y-2'>
                                        <div className='flex items-center gap-x-1'>
                                            <ChainToken
                                                tokenLogo={sellData.token?.logoURI}
                                                networkIcon={availableNetworks[sellData.chainId]?.icon}
                                                tokenIconSize={32}
                                                chainIconSize={12}
                                            />
                                            <span className='max-w-20 truncate text-2xl font-medium'>
                                                {sellData.token?.symbol}
                                            </span>
                                        </div>
                                        <span>{formatExchangeAmountWithCommas(sellData)}</span>
                                    </div>
                                    <div className='flex flex-1 flex-col items-center gap-3 py-1'>
                                        <div className='flex items-center'>
                                            <div
                                                className='h-[1px] w-1'
                                                style={{
                                                    background:
                                                        'linear-gradient(270deg, rgba(37, 38, 43, 0.7) 0%, rgba(125, 128, 145, 0.2) 100%)',
                                                }}
                                            ></div>
                                            <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#0F1011]'>
                                                <div className='flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#16171A]'>
                                                    <ExchangeIcon className='h-3 w-3' />
                                                </div>
                                            </div>
                                            <div
                                                className='h-[1px] w-1'
                                                style={{
                                                    background:
                                                        'linear-gradient(270deg, rgba(37, 38, 43, 0.7) 0%, rgba(125, 128, 145, 0.2) 100%)',
                                                }}
                                            ></div>
                                        </div>
                                        <div className='flex items-center gap-1'>
                                            <ClockIcon />
                                            <span className='text-[10px] font-medium text-mach-sub-text'>
                                                Arrival: {formatTime()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className='flex flex-col items-end gap-y-2'>
                                        <div className='flex items-center gap-x-1'>
                                            <ChainToken
                                                tokenLogo={buyData.token?.logoURI}
                                                networkIcon={availableNetworks[buyData.chainId]?.icon}
                                                tokenIconSize={32}
                                                chainIconSize={12}
                                            />
                                            <span className='max-w-20 truncate text-2xl font-medium'>
                                                {buyData.token?.symbol}
                                            </span>
                                        </div>
                                        <span>{formatExchangeAmountWithCommas(buyData)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Details Section */}
                        <div className='mt-8 flex flex-col gap-6'>
                            <span className='text-mach-accent-forground mb-2 text-lg font-medium'>Details</span>

                            {/* Order ID */}
                            <div className='flex items-center justify-between'>
                                <span className='text-sm text-mach-sub-text'>Order ID</span>
                                <div className='flex items-center gap-[6px]'>
                                    <CopyToClipboard anchorId='feather-order-id' textToCopy={orderId} />
                                    <span className='font-mono text-sm'>{truncateAddress(orderId)}</span>
                                </div>
                            </div>

                            {/* Source Transaction */}
                            {sourceTxHash && (
                                <div className='flex items-center justify-between'>
                                    <span className='text-sm text-mach-sub-text'>Source Transaction</span>
                                    <div className='flex items-center gap-[6px]'>
                                        <CopyToClipboard anchorId='source-tx-hash' textToCopy={sourceTxHash} />
                                        <span className='text-sm'>{formattedSourceTxHash}</span>
                                        {sourceTxExplorerUrl && (
                                            <a
                                                className='flex cursor-pointer text-mach-sub-text'
                                                href={sourceTxExplorerUrl}
                                                target='_blank'
                                                rel='noreferrer'
                                            >
                                                <UpArrowCorner className='h-3 w-3' />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Destination Transaction */}
                            {destinationTxHash && (
                                <div className='flex items-center justify-between'>
                                    <span className='text-sm text-mach-sub-text'>Destination Transaction</span>
                                    <div className='flex items-center gap-[6px]'>
                                        <CopyToClipboard anchorId='dest-tx-hash' textToCopy={destinationTxHash} />
                                        <span className='text-sm'>{formattedDestinationTxHash}</span>
                                        {destinationTxExplorerUrl && (
                                            <a
                                                className='flex cursor-pointer text-mach-sub-text'
                                                href={destinationTxExplorerUrl}
                                                target='_blank'
                                                rel='noreferrer'
                                            >
                                                <UpArrowCorner className='h-3 w-3' />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Feather Exchange Badge */}
                            <div className='flex items-center justify-between'>
                                <span className='text-sm text-mach-sub-text'>Exchange Type</span>
                                <span className='rounded-md bg-blue-500/20 px-2 py-0.5 text-xs text-blue-400'>
                                    Feather Order
                                </span>
                            </div>

                            {/* Miles earned */}
                            {miles !== undefined && (
                                <div className='flex items-center justify-between'>
                                    <span className='text-sm text-mach-sub-text'>Miles earned</span>
                                    <div className='flex items-center gap-[6px]'>
                                        <PointIcon height='32' width='32' />
                                        <span className='text-[28px]'>{formatNumber(miles?.toString())}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className='mt-6 flex justify-center'>
                <button
                    className='bg-mach-button-primary hover:bg-mach-button-hover w-full rounded-xl px-4 py-3 font-medium text-white transition-colors'
                    onClick={() => window.location.reload()}
                >
                    New Swap
                </button>
            </div>
        </div>
    )
}

export default FeatherSwapCompleted
